/**
 * The travel rate: time tagged with the travel activity bills at a share of
 * the normal rate (50% by default).
 *
 * KEYED OFF THE ACTIVITY, not a new toggle. Every surface that logs time — the
 * capture bar, the quick log, the timer, the phone — already lets you pick an
 * activity, so choosing "Travel" is all it takes and none of them needed a new
 * control. The entry editor has a checkbox for correcting one after the fact.
 *
 * ON THE ACCOUNT, not in localStorage: it changes what an invoice says, so the
 * phone and the laptop must agree. Stored on your own `users` record, which
 * only you can read or write.
 *
 * APPLIED ONCE, AT LOGGING. The discounted rate is what goes into the entry's
 * `rateSnapshot`, so every total, report and invoice reads it with no special
 * case — and `travelPct` records the share applied, so it can be undone
 * exactly even if the setting has changed since.
 */

import { useEffect, useState } from "react";

import { pb } from "@/lib/pocketbase";

export interface TravelSettings {
  /** Percent of the normal rate, 0–100. */
  pct: number;
  /** The activity that means travel. */
  activity: string;
}

export const TRAVEL_DEFAULTS: TravelSettings = { pct: 50, activity: "Travel" };

type WithTravel = { travelRatePct?: number | null; travelActivity?: string | null };

export function loadTravel(): TravelSettings {
  const u = (pb.authStore.model ?? {}) as WithTravel;
  const pct = typeof u.travelRatePct === "number" && u.travelRatePct > 0 ? u.travelRatePct : TRAVEL_DEFAULTS.pct;
  return { pct: Math.min(100, pct), activity: (u.travelActivity || "").trim() || TRAVEL_DEFAULTS.activity };
}

export function isTravelActivity(category: string | undefined, travel = loadTravel()): boolean {
  return !!category && category.trim().toLowerCase() === travel.activity.toLowerCase();
}

/** The rate to snapshot for travel time. */
export const travelRate = (rate: number, pct: number): number => Math.round(rate * pct) / 100;

export function useTravel(): [TravelSettings, (next: TravelSettings) => Promise<void>] {
  const [value, setValue] = useState(loadTravel);
  useEffect(() => pb.authStore.onChange(() => setValue(loadTravel())), []);

  async function save(next: TravelSettings) {
    const id = (pb.authStore.model as { id?: string } | null)?.id;
    if (!id) throw new Error("not signed in");
    const saved = await pb.collection("users").update(id, {
      travelRatePct: next.pct,
      travelActivity: next.activity.trim() || TRAVEL_DEFAULTS.activity,
    });
    // Refresh the signed-in record so loadTravel() sees the new value at once.
    pb.authStore.save(pb.authStore.token, saved as never);
    setValue(loadTravel());
  }

  return [value, save];
}
